import {
  Component, EventEmitter, Input, Output, inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ViewStates } from 'src/app/@lib/view-states';

export type UnavailableType = 'error' | 'empty' | 'forbidden';

@Component({
  selector: 'unavailable-block',
  standalone: false,
  styleUrls: ['./unavailable-block.component.scss'],
  templateUrl: './unavailable-block.component.html',
})
export class UnavailableBlockComponent {
  @Output() public refresh: EventEmitter<Event> = new EventEmitter<Event>();

  // full translate key of parent component (e.g. 'VIEW.RESTAURANT_LIST')
  // if translate key is set, it will look for
  // <translateKey>.<type.toUppercase()>.MESSAGE,
  // <translateKey>.<type.toUppercase()>.ICON
  // if translate key is  not set or key is not present in i18n json, it uses the default translations
  // TODO: currently doesn't update when language has been changed -> subscribe to language service
  @Input() public translateKey: string = '';

  // default is false
  // true when content is only accessible for authorized user or user with required permissions
  // handled in parent component / page
  @Input() public permissionDenied: boolean;

  public type: UnavailableType;
  public message: string;
  public icon: string;

  protected translate = inject(TranslateService);

  private _viewStates: ViewStates;

  @Input()
  public get viewStates(): ViewStates {
    return this._viewStates;
  }

  set viewStates(states: ViewStates) {
    if (!states) {
      this.type = null;

      return;
    }

    this.setType(states);
  }

  private setType(viewStates: ViewStates): void {
    if (this.permissionDenied) {
      this.type = 'forbidden';
    } else if (viewStates.hasError) {
      this.type = 'error';
    } else if (viewStates.isEmpty) {
      this.type = 'empty';
    } else {
      this.type = null;
    }

    this.setContent(this.type);
  }

  private setContent(type: UnavailableType): void {
    if (!this.type) {
      return;
    }

    // set default translations if custom translations are not available
    const key = `${this.translateKey}.${type.toUpperCase()}`;
    const messageKey = `${key}.MESSAGE`;
    const iconKey = `${key}.ICON`;

    if (!this.translateKey || this.translate.instant(key) === key) {
      this.message = this.translate.instant(`VIEW.COMMON.UNAVAILABLE.${type.toUpperCase()}`);
      this.icon = this.getIconNameByType(type);

      return;
    }

    this.message = this.translate.instant(messageKey);
    this.icon = this.translate.instant(iconKey);

    if (this.message === messageKey) {
      this.message = this.translate.instant(`VIEW.COMMON.UNAVAILABLE.${type.toUpperCase()}`);
    }

    if (this.translate.instant(iconKey) === iconKey) {
      this.icon = this.getIconNameByType(type);
    }
  }

  // see https://ionic.io/ionicons
  private getIconNameByType(type: UnavailableType): string {
    if (type === 'empty') {
      return 'leaf-outline';
    }

    if (type === 'forbidden') {
      return 'lock-closed-outline';
    }

    return 'alert-circle-outline';
  }
}
